import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  EmailTemplate,
  CreateEmailTemplateDto,
  UpdateEmailTemplateDto,
  EmailTemplatePreview,
  PreviewEmailTemplateDto,
} from '@yawi/types';
import type { PaginationMeta } from '@yawi/domain';
import { useServices, useToast } from '@/app/providers';

const EMPTY_PAGINATION: PaginationMeta = {
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

interface UseEmailTemplatesReturn {
  templates: EmailTemplate[];
  pagination: PaginationMeta;
  isLoading: boolean;
  isSaving: boolean;
  selectedType: string | null;
  setSelectedType: (type: string | null) => void;
  search: string;
  setSearch: (search: string) => void;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  createTemplate: (data: CreateEmailTemplateDto) => Promise<EmailTemplate>;
  updateTemplate: (id: string, data: UpdateEmailTemplateDto) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;
  duplicateTemplate: (id: string) => Promise<EmailTemplate>;
  setDefaultTemplate: (id: string) => Promise<void>;
  previewTemplate: (data: PreviewEmailTemplateDto) => Promise<EmailTemplatePreview>;
  getTemplateById: (id: string) => Promise<EmailTemplate>;
  refresh: () => void;
}

export function useEmailTemplates(): UseEmailTemplatesReturn {
  const { emailTemplateRepository } = useServices();
  const { showToast } = useToast();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>(EMPTY_PAGINATION);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Solo la última petición escribe el estado (respuestas fuera de orden).
  const requestIdRef = useRef(0);

  const fetchTemplates = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    try {
      const result = await emailTemplateRepository.findPaginated({
        page,
        limit: pageSize,
        search: search.trim() || undefined,
        code: selectedType ?? undefined,
      });
      if (requestId !== requestIdRef.current) return;
      setTemplates(result.data ?? []);
      setPagination(result.meta ?? EMPTY_PAGINATION);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error('Failed to fetch email templates:', err);
      showToast('error', 'Error al cargar plantillas de correo');
      setTemplates([]);
      setPagination(EMPTY_PAGINATION);
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, [emailTemplateRepository, page, pageSize, search, selectedType, showToast]);

  // Todo filtro o cambio de página recarga desde el servidor; sin esto la
  // tabla mostraba la página anterior con el filtro nuevo aplicado encima.
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleSetSelectedType = useCallback((type: string | null) => {
    setSelectedType(type);
    setPage(1);
  }, []);

  const handleSetPageSize = useCallback((size: number) => {
    setPageSize(size);
    setPage(1);
  }, []);

  const getTemplateById = useCallback(
    async (id: string): Promise<EmailTemplate> => {
      try {
        return await emailTemplateRepository.findById(id);
      } catch (err) {
        console.error('Failed to get email template:', err);
        const found = templates.find((t) => t.id === id);
        if (found) return found;
        throw err;
      }
    },
    [emailTemplateRepository, templates]
  );

  const createTemplate = useCallback(
    async (data: CreateEmailTemplateDto): Promise<EmailTemplate> => {
      setIsSaving(true);
      try {
        const template = await emailTemplateRepository.create(data);
        showToast('success', 'Plantilla creada exitosamente');
        fetchTemplates();
        return template;
      } catch (err) {
        console.error('Failed to create email template:', err);
        const message = err instanceof Error ? err.message : 'Error al crear plantilla';
        showToast('error', message);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [emailTemplateRepository, showToast, fetchTemplates]
  );

  const updateTemplate = useCallback(
    async (id: string, data: UpdateEmailTemplateDto) => {
      setIsSaving(true);
      try {
        await emailTemplateRepository.update(id, data);
        showToast('success', 'Plantilla actualizada exitosamente');
        fetchTemplates();
      } catch (err) {
        console.error('Failed to update email template:', err);
        const message = err instanceof Error ? err.message : 'Error al actualizar plantilla';
        showToast('error', message);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [emailTemplateRepository, showToast, fetchTemplates]
  );

  const deleteTemplate = useCallback(
    async (id: string) => {
      try {
        await emailTemplateRepository.delete(id);
        showToast('success', 'Plantilla eliminada');
        fetchTemplates();
      } catch (err) {
        console.error('Failed to delete email template:', err);
        const message = err instanceof Error ? err.message : 'Error al eliminar plantilla';
        showToast('error', message);
        throw err;
      }
    },
    [emailTemplateRepository, showToast, fetchTemplates]
  );

  const duplicateTemplate = useCallback(
    async (id: string): Promise<EmailTemplate> => {
      setIsSaving(true);
      try {
        const template = await emailTemplateRepository.duplicate(id);
        showToast('success', 'Plantilla duplicada exitosamente');
        fetchTemplates();
        return template;
      } catch (err) {
        console.error('Failed to duplicate email template:', err);
        const message = err instanceof Error ? err.message : 'Error al duplicar plantilla';
        showToast('error', message);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [emailTemplateRepository, showToast, fetchTemplates]
  );

  const setDefaultTemplate = useCallback(
    async (id: string) => {
      try {
        await emailTemplateRepository.setDefault(id);
        showToast('success', 'Plantilla marcada como predeterminada');
        fetchTemplates();
      } catch (err) {
        console.error('Failed to set default email template:', err);
        const message = err instanceof Error ? err.message : 'Error al marcar como predeterminada';
        showToast('error', message);
        throw err;
      }
    },
    [emailTemplateRepository, showToast, fetchTemplates]
  );

  const previewTemplate = useCallback(
    async (data: PreviewEmailTemplateDto): Promise<EmailTemplatePreview> => {
      try {
        return await emailTemplateRepository.preview(data);
      } catch (err) {
        console.error('Failed to preview email template:', err);
        const message = err instanceof Error ? err.message : 'Error al previsualizar plantilla';
        showToast('error', message);
        throw err;
      }
    },
    [emailTemplateRepository, showToast]
  );

  return {
    templates,
    pagination,
    isLoading,
    isSaving,
    selectedType,
    setSelectedType: handleSetSelectedType,
    search,
    setSearch: handleSearchChange,
    page,
    setPage,
    pageSize,
    setPageSize: handleSetPageSize,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    duplicateTemplate,
    setDefaultTemplate,
    previewTemplate,
    getTemplateById,
    refresh: fetchTemplates,
  };
}
